import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'

import { type EventInfo } from '~/features/general-participation/types/general-participation'
import { type OngoingEventResponse } from '~/features/main/apis/mainContents'
import TicketGeneralImageMobile from '~/shared/assets/images/ticket-general-mobile.png'
import TicketGeneralImagePc from '~/shared/assets/images/ticket-general-pc.png'
import useViewport from '~/shared/hooks/useViewport'
import theme from '~/shared/styles/theme'
import { fDateKo } from '~/shared/utils/format-date'

type Props = {
  ongoingEvent?: OngoingEventResponse
  data: EventInfo
  isOpen: boolean
}

function TicketBox({ ongoingEvent, data, isOpen }: Props) {
  const viewport = useViewport()
  const navigate = useNavigate()
  const isMobile = viewport < theme.deviceSizes.medium
  const backgroundImage = isMobile
    ? TicketGeneralImageMobile
    : TicketGeneralImagePc

  const handleClickTicketBuy = () => {
    navigate('/general-participation/ticket')
  }

  return (
    <Wrapper $image={backgroundImage}>
      <ContentBox>
        <Title>{`${ongoingEvent?.data.eventInfo.name} 입장권`}</Title>
        {!isMobile ? (
          <PeriodBox>
            <div>{fDateKo(data.start_date)}</div>
            <DashedLine />
            <div>{data.place}</div>
          </PeriodBox>
        ) : null}
        <TicketBuyButton
          $isOpen={isOpen}
          disabled={!isOpen}
          onClick={handleClickTicketBuy}
        >
          {isOpen ? (
            '티켓 구매하기'
          ) : (
            <StrikethroughText>티켓 신청 마감</StrikethroughText>
          )}
        </TicketBuyButton>
      </ContentBox>
    </Wrapper>
  )
}
export default TicketBox

const Wrapper = styled.div<{ $image: string }>`
  display: flex;
  flex-direction: column;

  width: 100%;
  height: 278px;

  background-repeat: no-repeat;
  ${(props) => `background-image: url(${props.$image});`};
  filter: drop-shadow(6px 7px 8px #c3c3c3);

  @media ${theme.device.medium} {
    width: 100%;
    height: 169px;
  }
`

const ContentBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 11px;

  width: fit-content;
  margin-top: 57px;
  margin-left: 117px;

  @media ${theme.device.medium} {
    width: 150px;
    margin-top: 28px;
    margin-left: 48px;
  }
`

const Title = styled.h2`
  font-family: NanumSquareNeo-Bold, sans-serif;
  font-size: 42px;

  @media ${theme.device.medium} {
    font-size: 21px;
    line-height: 29px;
  }
`

const DashedLine = styled.div`
  width: 134px;
  height: 0;
  border: 2px dashed #f6d9e3;
`

const PeriodBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const TicketBuyButton = styled.button<{ $isOpen: boolean }>`
  cursor: ${(props) => (props.$isOpen ? 'pointer' : 'not-allowed')};

  width: 194px;
  height: 58px;
  margin-top: 15px;

  font-family: ${(props) =>
    props.$isOpen
      ? 'NanumSquareNeo-Regular, sans-serif'
      : 'NanumSquareNeo-Bold, sans-serif'};
  font-size: 24px;
  font-weight: 500;
  line-height: 33px;
  color: ${(props) => (props.$isOpen ? 'white' : 'black')};

  background-color: ${(props) => (props.$isOpen ? 'black' : '#c3c3c3')};
  border: ${(props) => (props.$isOpen ? 'none' : '1px solid transparent')};
  border-radius: 54px;

  @media ${theme.device.medium} {
    width: 127px;
    height: 41px;

    font-size: 15px;
    line-height: 21px;

    border-radius: 21px;
  }
`
const StrikethroughText = styled.span`
  text-decoration: line-through;
`
